import PropTypes from 'prop-types';
import { Autocomplete, TextField, Chip } from '@mui/material';
import { sanitizeInput } from '../../utils/validation';
import { useTranslation } from 'react-i18next';

/**
 * Reusable Select Component with Search
 * Wraps Material UI Autocomplete with consistent styling
 * Automatically sanitizes input to prevent XSS attacks
 *
 * @param {String} label - Select label
 * @param {String|Number|Array} value - Selected value (array when multiple=true)
 * @param {Function} onChange - Change handler
 * @param {Array} options - Array of options {id, name} or {value, label}
 * @param {Boolean} required - Whether field is required
 * @param {Boolean} disabled - Whether select is disabled
 * @param {Boolean} fullWidth - Whether to take full width
 * @param {Object} sx - Additional Material UI sx styles
 * @param {String} placeholder - Placeholder text
 * @param {String} emptyMessage - Message when no options available
 * @param {Boolean} disableSanitize - Disable automatic HTML sanitization (use with caution)
 * @param {Boolean} multiple - Enable multiple selection
 */
function Select({
    label,
    value,
    onChange,
    options = [],
    required = false,
    disabled = false,
    fullWidth = true,
    sx = {},
    placeholder = '',
    emptyMessage,
    id,
    name,
    'data-testid': dataTestId,
    disableSanitize = false,
    multiple = false,
    ...otherProps
}) {
    const { t } = useTranslation();
    const localizedEmptyMessage = emptyMessage ?? t('table.noData');

    // Find the selected option object(s) from the value
    const getSelectedOption = () => {
        if (multiple) {
            // For multiple selection, value is an array
            if (!Array.isArray(value) || value.length === 0) return [];
            return options.filter((option) => {
                const optionValue = option.id ?? option.value;
                return value.some((v) => v === optionValue || String(v) === String(optionValue));
            });
        }
        // For single selection
        if (value === '' || value === null || value === undefined) return null;
        return options.find((option) => {
            const optionValue = option.id ?? option.value;
            return optionValue === value || String(optionValue) === String(value);
        }) || null;
    };

    const selectedOption = getSelectedOption();

    // Handle change event
    const handleChange = (event, newValue) => {
        if (multiple) {
            // For multiple selection, return array of values
            const selectedValues = newValue.map((option) => option.id ?? option.value);
            onChange(selectedValues);
        } else {
            // For single selection
            const selectedValue = newValue ? newValue.id ?? newValue.value : '';
            onChange({
                target: {
                    name: name,
                    value: selectedValue,
                },
            });
        }
    };

    // Handle input change to sanitize and prevent XSS
    const handleInputChange = (event, newInputValue, reason) => {
        // Only sanitize when user is typing (reason === 'input')
        if (!disableSanitize && newInputValue && reason === 'input') {
            const sanitizedValue = sanitizeInput(newInputValue);
            // If sanitization changed the value, input contained HTML tags like <script> - prevent it
            if (sanitizedValue !== newInputValue) {
                // Input contained HTML tags, prevent it by not updating
                return;
            }
        }
    };

    // Handle key down to prevent typing HTML tags like <script>
    const handleKeyDown = (event) => {
        if (!disableSanitize) {
            // Block the '<' character which is the start of HTML tags
            if (event.key === '<' || event.key === '>') {
                event.preventDefault();
                return;
            }
            // Block if user tries to paste HTML tags (Ctrl/Cmd + V)
            if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'v') {
                // We'll check the pasted content in handleInputChange
                // For now, just allow it and sanitize will handle it
            }
        }
    };

    return (
        <Autocomplete
            multiple={multiple}
            value={selectedOption}
            onChange={handleChange}
            onInputChange={handleInputChange}
            options={options}
            getOptionLabel={(option) => option.name ?? option.label ?? ''}
            isOptionEqualToValue={(option, value) => {
                const optionValue = option.id ?? option.value;
                const selectedValue = value?.id ?? value?.value;
                return optionValue === selectedValue || String(optionValue) === String(selectedValue);
            }}
            // Allow normal filtering for search functionality
            // Keep dropdown open when clicking for multiple selection
            disableCloseOnSelect={multiple}
            disabled={disabled}
            fullWidth={fullWidth}
            noOptionsText={localizedEmptyMessage}
            sx={sx}
            data-testid={dataTestId}
            renderTags={multiple ? (tagValue, getTagProps) =>
                tagValue.map((option, index) => (
                    <Chip
                        label={option.name ?? option.label}
                        size="small"
                        {...getTagProps({ index })}
                        key={option.id ?? option.value}
                    />
                ))
            : undefined}
            renderOption={(props, option, index) => {
                const optionValue = option.id ?? option.value;
                return (
                    <li {...props} key={`select-option-${optionValue}`} id={`select-option-${optionValue}`} data-testid={`select-option-${optionValue}`}>
                        {option.name ?? option.label}
                    </li>
                );
            }}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    required={required}
                    placeholder={multiple && selectedOption?.length > 0 ? '' : placeholder}
                    variant="outlined"
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    inputProps={{
                        ...params.inputProps,
                        id,
                        name,
                        'data-testid': dataTestId,
                        onKeyDown: (e) => {
                            // Call our handler first to block HTML tags
                            handleKeyDown(e);
                            // Then call the original handler if it exists
                            if (params.inputProps?.onKeyDown) {
                                params.inputProps.onKeyDown(e);
                            }
                        },
                    }}
                    sx={{
                        '& .MuiInputBase-root': {
                            padding: '1px 2px',
                            fontSize: '14px',
                            lineHeight: '1.5',
                        },
                        '& .MuiAutocomplete-input': {
                            padding: '7.5px 4px',
                        },
                    }}
                />
            )}
            {...otherProps}
        />
    );
}

Select.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.array]).isRequired,
    onChange: PropTypes.func.isRequired,
    options: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
            name: PropTypes.string,
            value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
            label: PropTypes.string,
        })
    ),
    required: PropTypes.bool,
    disabled: PropTypes.bool,
    fullWidth: PropTypes.bool,
    sx: PropTypes.object,
    placeholder: PropTypes.string,
    emptyMessage: PropTypes.string,
    id: PropTypes.string,
    name: PropTypes.string,
    'data-testid': PropTypes.string,
    disableSanitize: PropTypes.bool,
    multiple: PropTypes.bool,
};

export default Select;
